import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';

import useCompanySalaryChart, {
  SalariesChartData,
} from '~hooks/query/company/useCompanySalaryChart';
import { millionFormattedtoString } from '~utils/formatted/number';
import { isEqual } from '~utils/lodash';

import ButtonGroup from '../ButtonGroup';
import GuideMessage from '../GuideMessage';
import { useDisplayTrigger } from '../GuideMessage/hooks';
import { getIsEmptyData, getMergedDateArray } from '../helper';
import { useChartProps } from '../hooks';
import Legends from '../Legends';
import TooltipSalaries, { TooltipSalariesProps } from '../TooltipSalaries';
import { AvailableYears } from '../types';
import * as S from './style';

interface Props {
  companyId: string;
}

ChartJS.register(
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  Legend,
  PointElement,
  LineElement,
  Tooltip,
);

export default function SalaryChartContents({ companyId }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [year, setYear] = useState<AvailableYears>(3);
  const [tooltipProps, setTooltipProps] = useState<TooltipSalariesProps>();
  const [isEmptyData, setIsEmptyData] = useState<boolean>();
  const { data: originData } = useCompanySalaryChart(companyId);
  const [salariesChartData, setSalariesChartData] = useState<SalariesChartData[]>([]);
  const isDisplyGuide = useDisplayTrigger(wrapperRef);

  useEffect(() => {
    if (originData) {
      const mergedData = getMergedDateArray(year, originData);
      setSalariesChartData(mergedData);
      setIsEmptyData(getIsEmptyData(mergedData));
    }
  }, [originData, year]);

  const externalTooltipHandler = useCallback(
    (context) => {
      if (context.tooltip.opacity === 0) {
        const newTooltipProps = {
          ...tooltipProps,
          $opacity: 0,
        };

        if (!isEqual(tooltipProps, newTooltipProps)) {
          // @ts-ignore
          setTooltipProps(newTooltipProps);
        }

        return;
      }

      const { tooltip } = context;
      const { xAlign, dataPoints } = tooltip;
      const { x, y } = dataPoints[0].element;
      const [date] = tooltip.title;

      if (dataPoints[0] === undefined) {
        setTooltipProps(undefined);

        return;
      }

      const { salary } = dataPoints[0].raw;
      const formattedSalary = millionFormattedtoString(salary);

      const newTooltipProps = {
        $x: x,
        $y: y,
        $opacity: 1,
        xAlign,
        salary: formattedSalary,
        date,
      };

      if (!isEqual(tooltipProps, newTooltipProps)) setTooltipProps(newTooltipProps);
    },
    [year, tooltipProps, salariesChartData],
  );

  const { data, options, plugins, isInteracted } = useChartProps({
    originData: salariesChartData,
    year,
    externalTooltipHandler,
  });

  useEffect(() => {
    setTooltipProps(undefined);
  }, [data]);

  return (
    <S.Wrapper ref={wrapperRef}>
      <Legends />
      <S.ChartContainer>
        {salariesChartData && (
          <>
            {/* @ts-ignore */}
            <Line data={data} options={options} plugins={plugins} />
            {tooltipProps && <TooltipSalaries {...tooltipProps} />}
          </>
        )}

        {isEmptyData && (
          <S.NoData>
            최근 정보가 없습니다.
            <br /> 기간을 변경해보세요.
          </S.NoData>
        )}

        {isDisplyGuide && !isInteracted && !isEmptyData && <GuideMessage />}
      </S.ChartContainer>
      <ButtonGroup currentYear={year} onClick={(year) => setYear(year)} />
    </S.Wrapper>
  );
}
